export class Utils {

  static containsDigit = (value: string): boolean => {
    const regexp = /[0-9]/g;
    return regexp.test(value);
  };

  static containsUppercase = (value: string): boolean => {
    const regexp = /[A-Z]/g;
    return regexp.test(value);
  };

  static containsLowercase = (value: string): boolean => {
    const regexp = /[a-z]/g;
    return regexp.test(value);
  };

  static containsSpecialCharacter = (value: string): boolean => {
    const regexp = /[^a-zA-Z0-9]/g;
    return regexp.test(value);
  }

  static removeCountryCodeIfAny = (phoneNumber: string): string => {
    if (phoneNumber && phoneNumber.length > 10 && phoneNumber.startsWith("1")) {
      return phoneNumber.substring(phoneNumber.length - 10);
    }
    return phoneNumber;
  }

  private static _uniqueIdCounter = 0;
  static getUniqueId = () => {
    const uniqueId = ((Date.now() & 0xffffffff) + (++Utils._uniqueIdCounter)) & 0xffffffff;
    return uniqueId < 0 ? uniqueId : (uniqueId * -1);
  }
}

declare const google: any;

/**
 * Generates an array of random bytes. This is used as a workaround for the
 * lack of a `crypto` module in the browser.
 *
 * @param {number} n - The number of random bytes to generate.
 * @returns {Uint8Array} - An array of random bytes.
 */
function randomBytes(n: number): Uint8Array {
  const arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    arr[i] = Math.floor(Math.random() * 256);
  }
  return arr;
}

/**
 * Generate a unique client-side identifier.
 *
 * Used for the creation of new documents.
 *
 * @private
 * @see https://github.com/googleapis/nodejs-firestore/blob/main/dev/src/util.ts
 * @returns {string} A unique 20-character wide identifier.
 */
export function autoId(): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let autoId = '';
  while (autoId.length < 20) {
    const bytes = randomBytes(40);
    bytes.forEach(b => {
      // Length of `chars` is 62. We only take bytes between 0 and 62*4-1
      // (both inclusive). The value is then evenly mapped to indices of `char`
      // via a modulo operation.
      const maxValue = 62 * 4 - 1;
      if (autoId.length < 20 && b <= maxValue) {
        autoId += chars.charAt(b % 62);
      }
    });
  }
  return autoId;
}

const CACHE_STORAGE_KEY = 'streetview-cache';

async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function load_map_and_street_view_from_address(address, id, onPanoramaReady?,
  isGoogleStreetViewImageDisabled = false
) {

  if (!address || isGoogleStreetViewImageDisabled) {
    setTimeout(() => {
      showFallback(id);
    }, 250);
    return;
  }

  const key = await sha1Hex(address);
  const shaCacheKey = await sha1Hex(CACHE_STORAGE_KEY);

  const cacheString = localStorage.getItem(shaCacheKey);
  const cacheObj: Record<string, { panoId: string; pov: { heading: number; pitch: number; zoom: number } }> =
    cacheString ? JSON.parse(cacheString) : {};

  if (cacheObj[key]) {
    const { panoId, pov } = cacheObj[key];
    renderPanorama(id, panoId, pov, onPanoramaReady);
    return;
  }

  const svc = new google.maps.places.PlacesService(document.createElement('div'));

  svc.findPlaceFromQuery({
    query: address,
    fields: ['geometry']
  }, (results, status) => {
    if (status === google.maps.places.PlacesServiceStatus.OK) {
      const gps = results[0].geometry.location;
      loadStreetView(gps, id, key, onPanoramaReady);
    } else {
      showFallback(id);
    }
  });
}

function loadStreetView(gpsLatLng, id, cacheKey, onPanoramaReady?) {

  const panorama = new google.maps.StreetViewPanorama(
    document.getElementById(id)!,
    {
      visible: true,
      enableCloseButton: false,
      pov: { heading: 0, pitch: 0, zoom: 2 },
      addressControl: false,
      linksControl: false,
      disableDefaultUI: true,
      zoomControl: false,
      panControl: false,
      fullscreenControl: false,
      showRoadLabels: false
    }
  );

  const svc = new google.maps.StreetViewService();
  svc.getPanoramaByLocation(gpsLatLng, 100, (panoData, status) => {
    if (status !== google.maps.StreetViewStatus.OK) {
      showFallback(id);
      return;
    };

    panorama.setPano(panoData.location.pano);

    let heading = 0;
    if (google.maps.geometry && google.maps.geometry.spherical) {
      heading = google.maps.geometry.spherical.computeHeading(
        panoData.location.latLng,
        gpsLatLng
      );
    }
    const pov = { heading, pitch: 0, zoom: 2 };

    panorama.setPov(pov);

    if (typeof onPanoramaReady === 'function') {
      onPanoramaReady({
        panoId: panoData.location.pano,
        heading: pov.heading,
        pitch: pov.pitch,
        zoom: pov.zoom,
        lat: panoData.location.latLng.lat(),
        lng: panoData.location.latLng.lng()
      });
    }

    try {

      sha1Hex(CACHE_STORAGE_KEY).then((shaCacheKey) => {

        const cacheString = localStorage.getItem(shaCacheKey);
        const cacheObj: Record<string, { panoId: string; pov: { heading: number; pitch: number; zoom: number } }> =
          cacheString ? JSON.parse(cacheString) : {};

        cacheObj[cacheKey] = {
          panoId: panoData.location.pano,
          pov
        };

        localStorage.setItem(shaCacheKey, JSON.stringify(cacheObj));
      });


    } catch (e) {
      console.warn('StreetView cache failed:', e);
    }
  });
}

function renderPanorama(
  id: string,
  panoId: string,
  pov: { heading: number; pitch: number; zoom: number },
  onPanoramaReady?: (state: { panoId: string; heading: number; pitch: number; zoom: number; lat: number; lng: number }) => void
) {
  const panorama = new google.maps.StreetViewPanorama(
    document.getElementById(id)!,
    {
      visible: true,
      enableCloseButton: false,
      addressControl: false,
      linksControl: false,
      disableDefaultUI: true,
      zoomControl: false,
      panControl: false,
      fullscreenControl: false,
      showRoadLabels: false,
      pov: { heading: pov.heading, pitch: pov.pitch, zoom: pov.zoom }
    }
  );
  panorama.setPano(panoId);
  panorama.setPov(pov);
  if (typeof onPanoramaReady === 'function') {
    // We don't have lat/lng from cache, so just use 0/0 or try to extract from panorama if possible
    let lat = 0, lng = 0;
    try {
      const loc = panorama.getPosition();
      if (loc) {
        lat = loc.lat();
        lng = loc.lng();
      }
    } catch {
      // Street View metadata is best-effort; fall through with whatever we resolved.
    }
    onPanoramaReady({ panoId, heading: pov.heading, pitch: pov.pitch, zoom: pov.zoom, lat, lng });
  }
}

function showFallback(id: string) {
  const container = document.getElementById(id);
  container.innerHTML = '';
  const img = document.createElement('img');
  img.src = 'assets/images/street-view-fallback-img.png';
  img.alt = '';
  img.style.width = '100%';
  img.style.height = '100%';
  img.style.objectFit = 'cover';
  container.appendChild(img);
}

